import { useEffect, useState } from "react";
import { Lock, CheckCircle2, Download, Loader2, QrCode, Timer, TicketPercent, X, Star, ImagePlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { BACKEND, formatApiError } from "@/lib/api";
import { loadRazorpay } from "@/lib/razorpay";
import { fmtBytes, imageDims } from "@/lib/format";
import UpiPanel from "./UpiPanel";

export default function CheckoutModal({ product, open, onClose, upiSettings }) {
  const [step, setStep] = useState("details"); // details | upi | upi_pending | success
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [order, setOrder] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null); // {code, discount, final_price}
  const [couponBusy, setCouponBusy] = useState(false);
  const [review, setReview] = useState({ rating: 5, text: "" });
  const [reviewSent, setReviewSent] = useState(false);
  const [reviewPhoto, setReviewPhoto] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);

  const product_id = product?.id;

  const reset = () => {
    setStep("details");
    setBusy(false);
    setDone(null);
    setOrder(null);
    setApplied(null);
    setCoupon("");
    setReview({ rating: 5, text: "" });
    setReviewSent(false);
    setReviewPhoto(null);
  };

  const close = () => {
    onClose();
    setTimeout(reset, 250);
  };

  useEffect(() => {
    if (step !== "upi_pending" || !order) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/orders/${order.db_order_id}`);
        if (data.status === "paid") {
          clearInterval(t);
          setDone({ download_url: `/api/download/${order.db_order_id}`, order_id: order.db_order_id });
          setStep("success");
          toast.success("UPI payment verify ho gayi — download unlocked!");
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(t);
  }, [step, order]);

  const validDetails = () =>
    form.name.trim() && /^\S+@\S+\.\S+$/.test(form.email) && /^[0-9+\-\s]{10,15}$/.test(form.phone.trim());

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, product_id: product_id });
      setApplied(data);
      toast.success(`Coupon ${data.code} lagi — ₹${data.discount} ki bachat!`);
    } catch (e) {
      setApplied(null);
      toast.error(formatApiError(e));
    } finally {
      setCouponBusy(false);
    }
  };

  const uploadReviewPhoto = async (file) => {
    setPhotoBusy(true);
    try {
      const dims = await imageDims(file);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/reviews/upload", fd);
      setReviewPhoto({ url: data.url, name: file.name, size: data.size, ...(dims || {}) });
      toast.success("Photo attach ho gayi");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPhotoBusy(false);
    }
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", {
        product_id: product_id,
        name: form.name,
        rating: review.rating,
        text: review.text,
        order_id: done?.order_id || "",
        photo_url: reviewPhoto?.url || "",
      });
      setReviewSent(true);
      toast.success("Review mil gaya — jaldi storefront par dikhega!");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validDetails()) {
      toast.error("Naam, valid email aur 10-digit phone number bharein");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/create", { product_id: product_id, coupon_code: applied?.code || "", ...form });
      if (data.free) {
        setDone({ download_url: data.download_url, order_id: data.db_order_id });
        setStep("success");
        toast.success("Unlocked! Ab download karein.");
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) {
        toast.error("Razorpay load nahi hua — internet check karein");
        setBusy(false);
        return;
      }
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: "Voogly Education",
        description: product.title,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#F59E0B" },
        modal: { ondismiss: () => setBusy(false) },
        handler: async (res) => {
          try {
            const v = await api.post("/payments/verify", {
              db_order_id: data.db_order_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            setDone({ download_url: v.data.download_url, order_id: data.db_order_id });
            setStep("success");
            toast.success("Payment successful — download unlocked!");
          } catch (err) {
            toast.error(formatApiError(err));
          } finally {
            setBusy(false);
          }
        },
      });
      rzp.on("payment.failed", () => {
        toast.error("Payment failed — dobara try karein");
        setBusy(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const startUpi = async () => {
    if (!validDetails()) {
      toast.error("Naam, valid email aur 10-digit phone number bharein");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/upi-intent", { product_id: product_id, coupon_code: applied?.code || "", ...form });
      setOrder(data);
      setStep("upi");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!product) return null;
  const basePrice = product.price;
  const finalPrice = applied ? applied.final_price : basePrice;
  const priceLabel = finalPrice === 0 ? "Free" : `₹${finalPrice.toLocaleString("en-IN")}`;
  const upiReady = !!upiSettings?.upi_id && finalPrice > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="razorpay-checkout-modal">
        <div className="flex items-center gap-3 rounded-2xl bg-slate-50 p-4">
          {product.cover_image_url && (
            <img src={product.cover_image_url} alt="" className="h-14 w-14 rounded-xl object-cover" />
          )}
          <div className="min-w-0">
            <p className="font-display text-sm font-bold leading-snug line-clamp-2">{product.title}</p>
            <p className="mt-1 font-display text-lg font-extrabold text-amber-600">
              {finalPrice === 0 ? "Free" : `₹${finalPrice.toLocaleString("en-IN")}`}
              {applied && finalPrice > 0 && basePrice > finalPrice && (
                <span className="ml-2 text-sm font-semibold text-slate-400 line-through">₹{basePrice.toLocaleString("en-IN")}</span>
              )}
            </p>
          </div>
        </div>

        {step === "details" && (
          <form onSubmit={submit} className="mt-5 space-y-4">
            <DialogHeader>
              <DialogTitle className="font-display text-xl font-bold text-left">Apni details bharein</DialogTitle>
              <DialogDescription className="text-left text-sm">Download link isi email aur phone par milega.</DialogDescription>
            </DialogHeader>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="checkout-name-input"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20" />
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (download link yahan bhi jayega)" data-testid="checkout-email-input"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20" />
            <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d+\-\s]/g, "") })} placeholder="Mobile number (zaroori)" data-testid="checkout-phone-input"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20" />

            {basePrice > 0 && (
              <div>
                {applied ? (
                  <div className="flex items-center justify-between rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5" data-testid="coupon-applied-line">
                    <span className="inline-flex items-center gap-2 text-sm font-bold text-emerald-600">
                      <TicketPercent size={15} /> {applied.code} — ₹{applied.discount} off
                    </span>
                    <button type="button" onClick={() => setApplied(null)} className="text-slate-400 hover:text-red-500" data-testid="coupon-remove-btn" aria-label="Remove coupon">
                      <X size={15} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code (optional)" data-testid="coupon-input"
                      className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-amber-500" />
                    <button type="button" onClick={applyCoupon} disabled={couponBusy || !coupon.trim()} data-testid="coupon-apply-btn"
                      className="rounded-xl bg-ink px-5 py-3 text-xs font-bold text-amber-400 hover:bg-ink2 disabled:opacity-50">
                      {couponBusy ? <Loader2 size={14} className="animate-spin" /> : "Apply"}
                    </button>
                  </div>
                )}
              </div>
            )}

            <button type="submit" disabled={busy} data-testid="razorpay-pay-btn"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-500 py-3.5 font-display font-bold text-ink hover:bg-amber-400 transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={17} className="animate-spin" /> : <Lock size={15} />}
              {finalPrice === 0 ? "Unlock Free Download" : `Pay ${priceLabel} — Cards / NetBanking`}
            </button>
            {upiReady && (
              <button type="button" onClick={startUpi} disabled={busy} data-testid="upi-option-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full border border-ink/20 bg-white py-3 font-display text-sm font-bold text-ink hover:border-ink transition-colors">
                <QrCode size={15} /> UPI / QR se pay karein
              </button>
            )}
            <p className="text-center font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">UPI · Cards · NetBanking · Razorpay</p>
          </form>
        )}

        {step === "upi" && order && (
          <div className="mt-4">
            <DialogHeader>
              <DialogTitle className="font-display text-xl font-bold text-left">QR scan karke pay karein</DialogTitle>
              <DialogDescription className="text-left text-sm">Payment karne ke baad niche confirm karein.</DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              <UpiPanel order={order} product={product} onClaimed={() => setStep("upi_pending")} />
            </div>
          </div>
        )}

        {step === "upi_pending" && order && (
          <div className="mt-5 text-center" data-testid="upi-pending-state">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-cyan-500/15 text-cyan-500">
              <Timer size={30} />
            </span>
            <h3 className="mt-4 font-display text-xl font-extrabold">Payment verify ho rahi hai</h3>
            <p className="mt-2 text-sm text-slate-500">Owner payment dekh kar confirm karega — jaise hi hoga, download button yahan apne aap aa jayega.</p>
            <p className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 font-mono text-xs text-slate-600">Order ID: {order.db_order_id}</p>
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-400">
              <Loader2 size={14} className="animate-spin" /> Checking…
            </div>
          </div>
        )}

        {step === "success" && done && (
          <div className="mt-5 text-center" data-testid="checkout-success-state">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500">
              <CheckCircle2 size={34} />
            </span>
            <h3 className="mt-4 font-display text-xl font-extrabold">Payment complete!</h3>
            <p className="mt-2 text-sm text-slate-500">Aapki file unlock ho gayi — download button niche hai. Link aapke email par bhi ja chuka hai.</p>
            <a href={`${BACKEND}${done.download_url}`} download data-testid="checkout-download-btn"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-ink py-3.5 font-display font-bold text-amber-400 hover:bg-ink2">
              <Download size={17} /> Download Now
            </a>

            {done.order_id && !reviewSent && (
              <div className="mt-5 rounded-2xl bg-slate-50 p-4 text-left" data-testid="review-form">
                <p className="font-display text-sm font-bold text-slate-800">Rate karke doosre students ki madad karein</p>
                <div className="mt-2 flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setReview((r) => ({ ...r, rating: n }))} data-testid={`review-star-${n}`} aria-label={`${n} star`}>
                      <Star size={22} className={n <= review.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
                    </button>
                  ))}
                </div>
                <textarea value={review.text} onChange={(e) => setReview({ ...review, text: e.target.value })} placeholder="Kaisa laga? (optional)" data-testid="review-text-input"
                  className="mt-3 min-h-16 w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-amber-500" />

                <div className="mt-3">
                  {reviewPhoto ? (
                    <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2" data-testid="review-photo-chip">
                      <span className="flex items-center gap-2 min-w-0">
                        <img src={`${reviewPhoto.url.startsWith("/api") ? BACKEND : ""}${reviewPhoto.url}`} alt="" className="h-9 w-9 rounded-lg object-cover" />
                        <span className="font-mono text-[11px] text-slate-500 truncate">
                          {reviewPhoto.name}{reviewPhoto.w ? ` · ${reviewPhoto.w}×${reviewPhoto.h}` : ""} · {fmtBytes(reviewPhoto.size)}
                        </span>
                      </span>
                      <button type="button" onClick={() => setReviewPhoto(null)} className="text-slate-400 hover:text-red-500" data-testid="review-photo-remove-btn" aria-label="Remove photo">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-600 hover:border-amber-500 hover:text-amber-600" data-testid="review-photo-attach-btn">
                      {photoBusy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} Notes ki photo attach karein (optional)
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadReviewPhoto(e.target.files[0])} />
                    </label>
                  )}
                </div>

                <button type="button" onClick={submitReview} data-testid="review-submit-btn"
                  className="mt-3 w-full rounded-full bg-ink py-2.5 text-xs font-bold text-amber-400 hover:bg-ink2">
                  Review Bhejein
                </button>
              </div>
            )}
            {reviewSent && <p className="mt-4 text-sm font-semibold text-emerald-600" data-testid="review-thanks">Shukriya! Aapka review jald storefront par dikhega.</p>}

            <button onClick={close} className="mt-3 text-sm font-semibold text-slate-500 hover:text-slate-900" data-testid="checkout-close-btn">Close</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
